import React, { useMemo } from 'react';
import _ from 'lodash';
import i18next from 'i18next';
import HotelIcon from '@mui/icons-material/Hotel';
import SpaceIcon from '@mui/icons-material/FolderShared';
import { Box, Typography, Chip } from '@mui/material';
import { FormInputDropdown } from '../form/FormInputDropdown';
import { FormSelectOption } from '../form/FormInputProps';
import { userSelector } from 'helper/security';
import { unicodeIndent } from './SpaceSelectionInput';
import { ISpace, IHotel } from 'types';

interface HotelSelectionProps {
  hotels: IHotel[];
  allowSpaceSelection?: boolean;
  name: string;
  label: string;
  emptyLabel?: string | null;
  emptyValue?: any;
  control: any;
  [key: string]: any;
}

/**
 * Builds hotel selection options grouped by spaces with booking mode indicators.
 * Aggregates hasEpilo/hasSem flags from parent spaces for display.
 */
export const buildHotelSelectionOptions = (
  spaces: ISpace[],
  hotels: IHotel[],
  allowSpaceSelection: boolean,
  emptyLabel?: React.ReactNode,
  emptyValue?: any,
) => {
  const options = [] as FormSelectOption[];
  if (emptyLabel) {
    options.push({
      label: <em>{emptyLabel}</em>,
      value: emptyValue !== undefined ? emptyValue : 0,
    });
  }

  // Build a map of space ID to aggregated hasEpilo/hasSem including children
  const spaceMap = new Map(spaces.map(s => [s.id, s]));
  const aggregatedProps = new Map<number, { hasEpilo: boolean; hasSem: boolean }>();

  const getAggregatedProps = (space: ISpace): { hasEpilo: boolean; hasSem: boolean } => {
    if (aggregatedProps.has(space.id)) {
      return aggregatedProps.get(space.id)!;
    }

    let hasEpilo = space.hasEpilo || false;
    let hasSem = !!space.activeSEMPlan;

    // Check children - if any child has Epilo/SEM, parent should show it too
    if (space.children && space.children.length > 0) {
      for (const childRef of space.children) {
        const child = spaceMap.get(childRef.id);
        if (child) {
          const childProps = getAggregatedProps(child);
          hasEpilo = hasEpilo || childProps.hasEpilo;
          hasSem = hasSem || childProps.hasSem;
        }
      }
    }

    aggregatedProps.set(space.id, { hasEpilo, hasSem });
    return { hasEpilo, hasSem };
  };

  // Pre-compute aggregated props for all spaces
  spaces.forEach(s => getAggregatedProps(s));

  const _addSpace = (space: ISpace, level: number = 0) => {
    const spaceHotels = hotels.filter(h => h.space.id === space.id);
    const aggregated = aggregatedProps.get(space.id) || { hasEpilo: space.hasEpilo || false, hasSem: !!space.activeSEMPlan };

    options.push({
      label: (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pl: level * 2,
            py: 0.25,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: '6px',
              backgroundColor: 'primary.main',
              color: 'white',
              opacity: allowSpaceSelection ? 1 : 0.6,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <SpaceIcon sx={{ fontSize: '0.875rem' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontSize: '0.8125rem', color: allowSpaceSelection ? 'text.primary' : 'text.disabled' }}
            >
              {space.name}
            </Typography>
          </Box>
          {aggregated.hasEpilo && (
            <Chip
              label="S"
              size="small"
              sx={{
                height: 18,
                minWidth: 18,
                fontSize: '0.625rem',
                fontWeight: 600,
                backgroundColor: '#ac2a6e',
                color: 'white',
                '& .MuiChip-label': {
                  px: 0.5,
                },
              }}
            />
          )}
          {aggregated.hasSem && (
            <Chip
              label="S"
              size="small"
              sx={{
                height: 18,
                minWidth: 18,
                fontSize: '0.625rem',
                fontWeight: 600,
                backgroundColor: '#266670',
                color: 'white',
                '& .MuiChip-label': {
                  px: 0.5,
                },
              }}
            />
          )}
        </Box>
      ),
      filterText: space.name,
      value: allowSpaceSelection ? { spaceId: space.id, hotelId: null } : emptyValue !== undefined ? emptyValue : 0,
      disabled: !allowSpaceSelection,
    });

    for (const spaceHotel of spaceHotels) {
      options.push({
        label: (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pl: (level + 1) * 2 + 3,
              py: 0.25,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: (level + 1) * 2 + 1,
                top: '50%',
                width: '8px',
                height: '1px',
                backgroundColor: 'divider',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: '6px',
                border: '1.5px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                color: 'text.secondary',
              }}
            >
              <HotelIcon sx={{ fontSize: '0.85rem' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: '0.8125rem', color: 'text.primary' }}>
                {spaceHotel.name}
              </Typography>
              {spaceHotel.extRefCode && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                  {spaceHotel.extRefCode}
                </Typography>
              )}
            </Box>
          </Box>
        ),
        filterText: `${space.name} - ${spaceHotel.name}`,
        value: allowSpaceSelection ? { spaceId: space.id, hotelId: spaceHotel.id } : spaceHotel.id,
      });
    }

    if (space.children && space.children.length > 0) {
      const children = spaces.filter(fs => (space.children || []).findIndex(cc => fs.id == cc.id) >= 0);
      for (const child of children) {
        _addSpace(child, level + 1);
      }
    }
  };

  if (spaces.length > 0) {
    // Treat spaces as "root" if they have no parent OR if their parent is not in the spaces array
    const spaceIds = new Set(spaces.map(s => s.id));
    const effectiveRootSpaces = spaces.filter(s => !s.parent || !spaceIds.has(s.parent.id));
    for (const rootSpace of effectiveRootSpaces) {
      _addSpace(rootSpace, 0);
    }
  } else {
    options.push(
      ...hotels.map(h => ({
        label: (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.25,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: '6px',
                border: '1.5px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                color: 'text.secondary',
              }}
            >
              <HotelIcon sx={{ fontSize: '0.85rem' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: '0.8125rem', color: 'text.primary' }}>
                {h.name}
              </Typography>
              {h.extRefCode && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                  {h.extRefCode}
                </Typography>
              )}
            </Box>
          </Box>
        ),
        filterText: h.name,
        value: allowSpaceSelection ? { spaceId: h.space.id, hotelId: h.id } : h.id,
      })),
    );
  }
  return options;
};

/**
 * Form input component for selecting hotels grouped by spaces.
 * Shows booking mode indicators (EPILO/SEM) and supports optional space selection.
 */
export const HotelSelectionInput = (props: HotelSelectionProps) => {
  const { hotels, allowSpaceSelection, name, label, emptyLabel, emptyValue, control, ...rest } = props;
  const user = userSelector()!;

  const options = useMemo(
    () =>
      buildHotelSelectionOptions(user.spaces || [], hotels, !!allowSpaceSelection, emptyLabel || i18next.t('generic-selection-empty'), emptyValue),
    [user, hotels],
  );

  return (
    <FormInputDropdown
      name={name}
      control={control}
      label={label}
      options={options}
      filterPlaceholder={String(i18next.t('hotel-selection-search-placeholder'))}
      {...rest}
    />
  );
};
